'use client';

import {
  Accordion,
  Box,
  Carousel,
  type CarouselEvent,
  CarouselItem,
  CarouselRef,
  DrawerBody,
  Flex,
  freddyTheme,
  Link,
  Typography
} from '@coolblue-development/freddy';
import { ArrowLeft } from '@coolblue-development/icons';
import { useFreddyTranslations } from '@coolblue-development/next-design-system-utils/client';
import { PropertyExplanationArticle } from '@coolblue-development/next-hemingway/client';
import { useTranslation } from '@i18n/useTranslation';
import { useEffect, useRef } from 'react';

import { FilterPageContext, useFilterDrawer } from '../../../context';
import { Facet } from '../../../types';
import { MobileSorting } from '../../Sorting';
import MobileFacet from '../MobileFacet';

export default function Body() {
  const carouselTranslations = useFreddyTranslations('carousel');
  const { t } = useTranslation('webshop.filterPage.drawer');

  const ref = useRef<CarouselRef>(null);

  const { drawerContent, propertyExplanation, setPropertyExplanation } = useFilterDrawer();
  const { facets, ...filterPageContextValue } = drawerContent || { facets: [] };

  function handleScrollEnd(event: CarouselEvent) {
    if (event.index === 0) {
      setPropertyExplanation(null);
    }
  }

  useEffect(() => {
    if (propertyExplanation) {
      ref.current?.goToItem(1);
    }
  }, [propertyExplanation]);

  const accordionContent = facets
    ?.filter((facet: Facet) => Boolean(facet.facetName))
    ?.map((facet: Facet) => <MobileFacet key={facet.facetName} facet={facet} />);

  return (
    <DrawerBody noPadding>
      <Carousel
        itemsInView={1}
        hideNavigation
        ref={ref}
        translations={carouselTranslations}
        onScrollEnd={handleScrollEnd}
        sx={{ height: '100%', ul: { height: '100%' } }}
      >
        <CarouselItem>
          <Box sx={{ height: '100%', overflowY: 'scroll' }}>
            <FilterPageContext.Provider
              value={{ ...filterPageContextValue, trackingSubFeature: 'filter_and_sort_overlay' }}
            >
              <Accordion variant='with-dividers' sx={accordionStyles}>
                <MobileSorting />
                {accordionContent}
              </Accordion>
            </FilterPageContext.Provider>
          </Box>
        </CarouselItem>

        {propertyExplanation ? (
          <CarouselItem>
            <Flex direction='column' gap={300} p={300} sx={{ height: '100%' }}>
              <Link
                component='button'
                startIcon={<ArrowLeft />}
                onClick={() => {
                  ref.current?.goToItem(0);
                }}
              >
                {t('backToFilters')}
              </Link>

              <Flex direction='column' gap={400} sx={{ position: 'relative' }} flex={1}>
                <Typography variant='h2'>{propertyExplanation.title}</Typography>
                <PropertyExplanationArticle {...propertyExplanation} />
              </Flex>
            </Flex>
          </CarouselItem>
        ) : (
          <></>
        )}
      </Carousel>
    </DrawerBody>
  );
}

const accordionStyles = {
  details: {
    padding: `0 ${freddyTheme.spacing[300]} !important`,
    '&:first-of-type': {
      borderTop: 'none'
    },
    'summary:hover': {
      textDecoration: 'none'
    },
    '.selected-filters': {
      transition: 'max-height 0.2s ease, transform 0.25s ease, opacity 0.35s ease',
      transform: 'translateY(0)',
      maxHeight: freddyTheme.typography.lineHeight[500],
      opacity: 1
    },
    '&[open] .selected-filters': {
      transform: 'translateY(50%)',
      maxHeight: '0',
      opacity: 0
    }
  }
};
